import { queryClient } from "@/config/react-query";

import { PackageStatus } from "@/types/enums/package-status";
import {
  PackageDetail,
  PackageFilters,
  PatientSoldPackage,
} from "@/types/inventory/package";
import { Page } from "@/types/pagination";

import { listPackages } from "./package";
import {
  optimisticAdjustSidebarCounts,
  revertSidebarCounts,
} from "@/api/provider/user/patient/sidebar-cache-utils";

export const packageQueryKeys = {
  all: ["inventory", "packages"] as const,
  list: (filters?: PackageFilters) =>
    [...packageQueryKeys.all, "list", filters] as const,
  categories: () => [...packageQueryKeys.all, "categories"] as const,
};

export const packageQueryOptions = {
  list: (filters?: PackageFilters) => ({
    queryKey: packageQueryKeys.list(filters),
    queryFn: () => listPackages(filters),
    select: (data: Page<PackageDetail> | null) => data?.data ?? [],
    staleTime: 1000 * 60 * 5, // 5 minutes
  }),
};

export const patientSoldPackageQueryKeys = {
  root: ["patient-packages"] as const,
  all: (patientId: string) =>
    [...patientSoldPackageQueryKeys.root, patientId] as const,
  active: (patientId: string) =>
    [...patientSoldPackageQueryKeys.all(patientId), "active"] as const,
};

type PatientSoldPackagePatch = Partial<
  Pick<PatientSoldPackage, "status" | "cancellationReason" | "expiresAt">
>;

const applyPatchInList = (
  list: PatientSoldPackage[] | undefined,
  packageId: string,
  patch: PatientSoldPackagePatch,
): PatientSoldPackage[] | undefined =>
  list?.map((p) => (p.id === packageId ? { ...p, ...patch } : p));

/**
 * Optimistically reflects a sold-package patch across every cache the UI
 * reads from: the patient sidebar count badge, the sidebar's active-only
 * list, and the packages tab's full list. Returns a revert function that
 * restores all three on failure.
 */
export function optimisticAdjustPatientPackage(
  patientId: string,
  pkg: PatientSoldPackage,
  patch: PatientSoldPackagePatch,
): () => void {
  const oldStatus = pkg.status;
  const newStatus = patch.status ?? oldStatus;
  const allKey = patientSoldPackageQueryKeys.all(patientId);
  const activeKey = patientSoldPackageQueryKeys.active(patientId);

  const previousAll = queryClient.getQueryData<
    PatientSoldPackage[] | undefined
  >(allKey);
  const previousActive = queryClient.getQueryData<
    PatientSoldPackage[] | undefined
  >(activeKey);

  queryClient.setQueryData(
    allKey,
    applyPatchInList(previousAll, pkg.id, patch),
  );

  if (
    newStatus === PackageStatus.ACTIVE &&
    oldStatus !== PackageStatus.ACTIVE
  ) {
    queryClient.setQueryData(activeKey, [
      ...(previousActive ?? []),
      { ...pkg, ...patch },
    ]);
  } else if (
    oldStatus === PackageStatus.ACTIVE &&
    newStatus !== PackageStatus.ACTIVE
  ) {
    queryClient.setQueryData(
      activeKey,
      previousActive?.filter((p) => p.id !== pkg.id),
    );
  } else {
    queryClient.setQueryData(
      activeKey,
      applyPatchInList(previousActive, pkg.id, patch),
    );
  }

  const countDelta =
    newStatus === PackageStatus.ACTIVE && oldStatus !== PackageStatus.ACTIVE
      ? 1
      : oldStatus === PackageStatus.ACTIVE && newStatus !== PackageStatus.ACTIVE
        ? -1
        : 0;
  const previousSidebarCounts = countDelta
    ? optimisticAdjustSidebarCounts(patientId, { packagesCount: countDelta })
    : undefined;

  return () => {
    queryClient.setQueryData(allKey, previousAll);
    queryClient.setQueryData(activeKey, previousActive);
    if (countDelta) revertSidebarCounts(patientId, previousSidebarCounts);
  };
}
